"use client";

import type { IconType } from "react-icons";
import { LuClock, LuDollarSign, LuGem } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/utils/lib";

export const orderByPresets = {
  wealth: {
    label: "Most Valuable",
    icon: LuDollarSign,
  },
  latest: {
    label: "Latest",
    icon: LuClock,
  },
  diamonds: {
    label: "Most Diamonds",
    icon: LuGem,
  },
} as const;

export type OrderByPreset = keyof typeof orderByPresets;

type CommentSortProps = {
  orderByPreset: OrderByPreset;
  onOrderChange: (value: OrderByPreset) => void;
};

export const CommentSort = ({
  orderByPreset,
  onOrderChange,
}: CommentSortProps) => {
  const currentPreset = orderByPresets[orderByPreset];
  const CurrentIcon = currentPreset.icon as IconType;
  const triggerContent = (
    <span className="mr-1 inline-flex items-center gap-1.5 text-xs">
      <CurrentIcon className="size-3.5 shrink-0" />
      <span className="relative top-px">{currentPreset.label}</span>
    </span>
  );

  return (
    <Select
      onValueChange={(v) => {
        onOrderChange(v as OrderByPreset);
      }}
      value={orderByPreset}
    >
      <SelectTrigger
        className="
          ml-auto w-auto rounded-none border-none bg-transparent px-0 py-2
          text-xs leading-none text-muted hover:border-transparent
          hover:bg-transparent hover:text-foreground
          focus:ring-0
        "
      >
        {orderByPreset === "wealth" && (
          <div
            className="
              hidden
              md:block
            "
          >
            <InfoTooltip
              className="max-w-64"
              side="left"
              text={
                <>
                  Upvote wealth is the combined wealth of everyone who's
                  up-voted a piece of content. Down-votes count as negative.
                </>
              }
            >
              {triggerContent}
            </InfoTooltip>
          </div>
        )}

        <div
          className={cn(
            `
              inline
              md:hidden
            `,
            {
              "md:inline": orderByPreset !== "wealth",
            },
          )}
        >
          {triggerContent}
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="wealth">
          <div className="flex items-center gap-2">
            <LuDollarSign className="size-4 shrink-0" />
            {orderByPresets.wealth.label}
          </div>
        </SelectItem>
        <SelectItem value="latest" className="flex gap-2">
          <div className="flex items-center gap-2">
            <LuClock className="size-4 shrink-0" />
            {orderByPresets.latest.label}
          </div>
        </SelectItem>
        <SelectItem value="diamonds">
          <div className="flex items-center gap-2">
            <LuGem className="size-4 shrink-0" />
            {orderByPresets.diamonds.label}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};
